import { Component, OnInit } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
  AppBarAction,
  AppBarActionsService,
  AppBarTitleService,
  AppPageService,
  EnumHelper,
  FormHelper,
  MessageService,
  parseNumber,
  ProductName,
  CommonActionKey,
  SimpleDialogService,
} from "common";
import { readWrite } from "../../../../user/user-permission/user-permission.data";
import { UserPermissionService } from "../../../../user/user-permission/user-permission.service";
import { FacilitiesFacade } from "../../domain/+state/facilities-facade";
import { ActivatedRoute } from "@angular/router";
import { FacilityDetailsFacade } from "./facility-details.facade";
import { map } from "rxjs/operators";
import { forkJoin, Observable, of } from "rxjs";
import { FacilitiesActiveLabelValues, FacilityDetailsData, FacilityInputData, SecCode, SecCodeLabel } from "../../domain/models/facility.model";
import { UntypedFormArray, UntypedFormGroup } from "@angular/forms";

@UntilDestroy()
@Component({
    selector: "ifb-facility-details",
    templateUrl: "./facility-details.component.html",
    styleUrls: ["./facility-details.component.scss"],
    providers: [FacilityDetailsFacade],
    standalone: false
})
export class FacilityDetailsComponent implements OnInit {
  readonly FacilitiesActiveLabelValues = FacilitiesActiveLabelValues;
  readonly productTypeOptions = EnumHelper.getNamesAndValues(ProductName);
  readonly secCodeOptions = EnumHelper.getMappedNamesAndValues(SecCode, SecCodeLabel);

  data: FacilityInputData = {
    dataSource: undefined,
    isWriteAllowed: false,
    isCreating: true,
  };

  form: UntypedFormGroup;
  showLenders = true;
  hideInbound = false;
  seniorLendersArray: UntypedFormArray;
  mezzanineLendersArray: UntypedFormArray;

  private isInitializingForm = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly facilitiesFacade: FacilitiesFacade,
    private readonly facilityDetailsFacade: FacilityDetailsFacade,
    private readonly userPermissionService: UserPermissionService,
    private readonly messageService: MessageService,
    private readonly appPageService: AppPageService,
    private readonly appBarActionsService: AppBarActionsService,
    private readonly appBarTitleService: AppBarTitleService,
    private readonly simpleDialogService: SimpleDialogService
  ) {
    this.initForm();
    this.initAppActions();
  }

  ngOnInit(): void {
    this.initData();
    this.watchAppBarActions();
  }

  addSeniorLender(): void {
    this.seniorLendersArray.push(this.facilityDetailsFacade.createLender());
  }

  removeSeniorLender(index: number): void {
    this.seniorLendersArray.removeAt(index);
    this.seniorLendersArray.markAsDirty();
  }

  addMezzanineLender(): void {
    this.mezzanineLendersArray.push(this.facilityDetailsFacade.createLender());
  }

  removeMezzanineLender(index: number): void {
    this.mezzanineLendersArray.removeAt(index);
    this.mezzanineLendersArray.markAsDirty();
  }

  actionDispatch(action: AppBarAction): void {
    const actionHandler: (action: AppBarAction) => void =
      this[action.id].bind(this);
    actionHandler(action);
  }

  cancel(): void {
    this.appPageService.back();
  }

  delete(): void {
    const message = `Are you sure you want to remove facility ${this.data?.dataSource?.name}?`;
    this.simpleDialogService.openSimpleConfirmDialog(message, "Confirm")
      .subscribe((result: CommonActionKey) => {
        if (result === CommonActionKey.Confirm) {
          this.facilitiesFacade.removeFacility(this.data?.dataSource?.id).subscribe({
            next: () => this.handleSuccessRemoveFacility(),
            error: (err) => this.handleErrorRemoveFacility(err)
          });
        }
      });
  }

  save(): void {
    FormHelper.showInvalidFormFields(this.form);

    if (!this.form.valid) return;

    if (this.data.isCreating) {
      this.addNewFacility();
    }
    else {
      this.updateFacility();
    }
  }

  private updateFacility(): void {
    const message = `Are you sure you want to update facility ${this.data?.dataSource?.name}?`;
    this.simpleDialogService.openSimpleConfirmDialog(message, "Confirm")
      .subscribe((result: CommonActionKey) => {
        if (result === CommonActionKey.Confirm) {
          this.facilitiesFacade
            .updateFacility(this.data?.dataSource?.id, this.facilityDetailsFacade.formToFacilityData(this.form, true))
            .pipe(untilDestroyed(this))
            .subscribe({
              next: () => this.handleSuccessUpdateFacility(),
              error: (err) => this.handleErrorUpdateFacility(err),
            });
        }
      });
  }

  private addNewFacility(): void {
    this.simpleDialogService.openSimpleConfirmDialog("Add new facility?", "Confirm")
      .subscribe((result: CommonActionKey) => {
        if (result === CommonActionKey.Confirm) {
          this.facilitiesFacade
            .createFacility(this.facilityDetailsFacade.formToFacilityData(this.form, false))
            .pipe(untilDestroyed(this))
            .subscribe({
              next: () => this.handleSuccessCreateFacility(),
              error: (err) => this.handleErrorCreateFacility(err),
            });
          return;
        }
      });
  }

  private initForm(): void {
    this.form = this.facilityDetailsFacade.initForm();
    this.seniorLendersArray = this.form.get('seniorLenders.lendersNames') as UntypedFormArray;
    this.mezzanineLendersArray = this.form.get('mezzanineLenders.lendersNames') as UntypedFormArray;
    this.addSeniorLender();
    this.watchFormChanges();
  }

  private watchFormChanges(): void {
    this.form.get('isDefault')?.valueChanges.pipe(untilDestroyed(this)).subscribe((isDefault: boolean) => {
      this.facilityDetailsFacade.toggleDefaultFacilityMode(this.form, isDefault);
      this.showLenders = !isDefault;
    });

    this.form.get('inboundBankAccount.sameAsOutbound')?.valueChanges.pipe(untilDestroyed(this)).subscribe((isSame: boolean) => {
      this.facilityDetailsFacade.toggleInboundAccountSync(this.form, isSame);
      this.hideInbound = isSame
    });

    this.form.get('mezzanineLenders.percentage')?.valueChanges.pipe(untilDestroyed(this)).subscribe((percentage: number) => {
      if (this.mezzanineLendersArray?.length === 0 && percentage > 0) {
        this.addMezzanineLender();
      }
      this.facilityDetailsFacade.changeMezzanineLendersNamesRequirement(this.form, percentage);
    })

    this.form.get('mezzanineLenders.lendersNames')?.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.facilityDetailsFacade.changeMezzanineLendersPercentageRequirement(this.form, this.mezzanineLendersArray);
    })
  }

  private initData(): void {
    const facilityIdRaw = this.route.snapshot.paramMap.get("id");
    const isNew = !facilityIdRaw || facilityIdRaw === "new";

    if (isNew) {
      this.data = {
        dataSource: undefined,
        isWriteAllowed: true,
        isCreating: true
      };
      this.appBarTitleService.title = "Create new Facility";
      this.toggleAppBarActions();
      return;
    }

    forkJoin([this.initPermissions(), this.initRouteParams()])
      .pipe(untilDestroyed(this))
      .subscribe(
        ([permissions, params]) => {
          this.isInitializingForm = true;

          this.data = {
            dataSource: params.facilityData,
            isCreating: params.isCreating,
            isWriteAllowed: permissions,
          };

          this.appBarTitleService.title = this.data.dataSource.name;

          if (!this.data.isCreating && this.data.dataSource) {
            this.facilityDetailsFacade.patchFormFromFacility(this.form, this.data.dataSource);
          }

          this.toggleAppBarActions();

          if (!this.data.isWriteAllowed) {
            this.form.disable({ emitEvent: false });
          }
          this.form.markAsPristine();
          this.isInitializingForm = false;
        }
      );
  }

  private initPermissions(): Observable<boolean> {
    return this.userPermissionService
      .granted([readWrite("admin-facilities")])
      .pipe(untilDestroyed(this));
  }

  private toggleAppBarActions(): void {
    this.appBarActionsService.enable("save", this.data.isCreating);

    this.appBarActionsService.hide("delete", !this.data.isWriteAllowed || this.data.isCreating);
    this.appBarActionsService.hide("save", !this.data.isWriteAllowed);
  }

  private initRouteParams(): Observable<{ facilityData: FacilityDetailsData, isCreating: boolean }> {
    const idRaw = this.route.snapshot.paramMap.get("id");
    const isCreating = idRaw === "new";
    const id = parseNumber(idRaw);

    if (isCreating) {
      return of({ facilityData: undefined, isCreating });
    }

    return this.facilitiesFacade.getFacilityById(id).pipe(
      untilDestroyed(this),
      map((facilityData) => {
        return { facilityData, isCreating };
      },)
    );
  }

  private initAppActions(): void {
    this.facilityDetailsFacade.initAppBarActions();
  }

  private watchAppBarActions(): void {
    this.appBarActionsService.invoking
      .pipe(untilDestroyed(this))
      .subscribe(this.actionDispatch.bind(this));

    this.form.controls.seniorLenders.statusChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.form.controls.seniorLenders.markAsDirty();
        this.form.markAsDirty();
        this.form.updateValueAndValidity();
      })

    this.form.controls.mezzanineLenders.statusChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.form.controls.mezzanineLenders.markAsDirty();
        this.form.markAsDirty();
        this.form.updateValueAndValidity();
      })

    this.form.statusChanges
      .pipe(untilDestroyed(this))
      .subscribe((newStatus: string) => {
        if (this.isInitializingForm || this.data.isCreating) return;

        const isEnabled =
          newStatus === "VALID" &&
          (this.form.dirty) &&
          this.data.isWriteAllowed;
        this.appBarActionsService.enable("save", isEnabled);
      });
  }

  private handleSuccessCreateFacility(): void {
    this.messageService.success(
      "Facility has been successfully created."
    );
    this.appPageService.back();
  }

  private handleErrorCreateFacility(err): void {
    this.messageService.error(`Facility cannot be created. ${err.error?.message ?? ''}`);
  }

  private handleSuccessRemoveFacility(): void {
    this.messageService.success(
      "Facility has been successfully removed."
    );
    this.appPageService.back();
  }

  private handleErrorRemoveFacility(err): void {
    this.messageService.error(`Facility cannot be removed. ${err.error?.message ?? ''}`);
  }

  private handleSuccessUpdateFacility(): void {
    this.messageService.success(
      "Facility has been successfully updated."
    );
    this.appPageService.back();
  }

  private handleErrorUpdateFacility(err): void {
    this.messageService.error(`Facility cannot be updated. ${err.error?.message ?? ''}`);
  }
}
